// ==========================================
// UPDATED hooks/useMessages.js
// Gracefully handles anonymous users (no token)
// ==========================================
import { useState, useCallback, useEffect } from 'react';
import { useAPI } from './useAPI';
import { UNREAD_MESSAGES_POLL_INTERVAL } from '../utils/constants';

export const useMessages = () => {
  const { apiCall } = useAPI();
  const [conversations, setConversations] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const isAuthenticated = () => !!localStorage.getItem('token');

  const loadConversations = useCallback(async () => {
    if (!isAuthenticated()) return;
    try {
      const data = await apiCall('/messages/conversations');
      setConversations(data);
    } catch (err) {
      console.error('Error loading conversations:', err);
    }
  }, [apiCall]);

  const loadUnreadCount = useCallback(async () => {
    if (!isAuthenticated()) return;
    try {
      const data = await apiCall('/messages/unread');
      setUnreadCount(data.unread_count);
    } catch (err) {
      // Silently fail for unread count - don't spam console
    }
  }, [apiCall]);

  const sendMessage = useCallback(async (receiverId, content) => {
    if (!isAuthenticated()) throw new Error('Login required to send messages');
    try {
      const message = await apiCall('/messages', {
        method: 'POST',
        body: JSON.stringify({ receiver_id: receiverId, content }),
      });
      return message;
    } catch (err) {
      throw err;
    }
  }, [apiCall]);

  const getConversation = useCallback(async (friendId) => {
    if (!isAuthenticated()) throw new Error('Login required');
    try {
      const data = await apiCall(`/messages/conversation/${friendId}`);
      return data.messages;
    } catch (err) {
      throw err;
    }
  }, [apiCall]);

  useEffect(() => {
    if (!isAuthenticated()) return;

    loadConversations();
    loadUnreadCount();

    const interval = setInterval(loadUnreadCount, UNREAD_MESSAGES_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [loadConversations, loadUnreadCount]);

  return {
    conversations,
    unreadCount,
    loadConversations,
    sendMessage,
    getConversation,
  };
};